import Vue from 'vue'
import Vuex from 'vuex'
import { format, addDays } from 'date-fns'
import { IS_PRODUCTION } from './libs/constants'
import { AB_PLAN_NUMBER, getNormalizedPlanData } from './libs/plan-helper'
import { getUserInfo } from '@/api/user'
import { getCurrentNext } from '@/api/userPlan'
import { getList as getABList } from '@/api/abtest'
import { getMineCount } from '@/api/ai'

Vue.use(Vuex)

const now = new Date()

const store = new Vuex.Store({
  state: {
    errorData: null,
    layout: {
      gnbMinimize: false,
      lnbMinimize: false
    },
    pageAddGuideFocusContent: {
      currentStep: 1,
      currentContentIndex: 'default'
    },
    currentDashboardCategory: {},
    isFirstLogin: false,
    stickyData: null,
    stickyDataId: null,
    showStickyData: false,
    showNoPvDialog: true,
    userData: null,
    planData: {},
    isDashboardEditMode: false,
    pageList: [],
    applyData: null,
    funnelData: null,
    ctaData: {},
    reportPeriod: {
      start: format(addDays(now, -6), 'YYYY-MM-DD'),
      end: format(now, 'YYYY-MM-DD')
    },
    abList: null,
    abPageName: null,
    showUserMenu: false,
    showReportPageList: false,
    showABTestTip: true,
    resizeTimestamp: 0,
    captureImageTimestamp: new Date().getTime(),
    myInfoTimestamp: null,
    dreamVersion: null,
    isABPlan: false,
    timeseriesData: null,
    freqRangeSelect: {},
    freqSelect: {},
    freqRange: false,
    isDemo: false,
    cxReportId: null,
    abEditValues: null,
    selectedReport: [],
    draggingItem: {},
    isChatOpened: false,
    totalReportCnt: 0,
    totalRemainJam: 0,
    applyBasketCount: 0,
    jamListFilter: []
  },
  getters: {
    creditData (state) {
      const result = {
        hasPlan: false,
        isUnlimited: false,
        remain: 0,
        percentage: 0
      }

      const userData = state.userData
      const planData = state.planData

      if (userData && planData) {
        result.hasPlan = planData.hasPlan

        if (!userData.creditEmpty && planData.hasPlan) {
          result.remain = userData.remainPv

          if (result.remain === -1) {
            result.isUnlimited = true
            result.percentage = 100
          } else {
            result.percentage = userData.battery || 0
          }
        }
      }
      return result
    },
    abUsedTraffic (store) {
      if (!store.abList) {
        return 0
      }

      let totalTraffic = 0
      for (let i = 0, iLen = store.abList.length; i < iLen; i++) {
        const item = store.abList[i]
        const state = item.state
        const traffic = item.traffic
        if (!['endByPvLimit', 'endByAnalysisDate'].includes(state)) {
          totalTraffic = totalTraffic + traffic
        }
      }
      return Math.min(totalTraffic, 100)
    },
    lnbMinimize (state) {
      return state.layout.lnbMinimize
    }
  },
  mutations: {
    setIsDemo (state, value) {
      state.isDemo = value
    },
    setUserData (state, value) {
      state.userData = value
    },
    setPlanData (state, value) {
      state.planData = value
    },
    setErrorData (state, payload) {
      state.errorData = payload
    },
    setLnbMinimize (state) {
      state.layout.lnbMinimize = !state.layout.lnbMinimize
    },
    setGnbMinimize (state) {
      state.layout.gnbMinimize = !state.layout.gnbMinimize
    },
    setCurrentDashboardCategory (state, payload) {
      state.currentDashboardCategory = payload
    },
    setIsFirstLogin (state, payload) {
      state.isFirstLogin = payload
    },
    setShowUserMenu (state, value) {
      state.showUserMenu = value
    },
    setIsDashboardEditMode (state, payload) {
      state.isDashboardEditMode = payload
    },
    setShowReportPageList (state, value) {
      state.showReportPageList = value
    },
    setReportPeriod (state, value) {
      state.reportPeriod = value
    },
    setApplyData (state, value) {
      state.applyData = value
    },
    setFunnelData (state, value) {
      state.funnelData = value
    },
    setCtaData (state, payload) {
      state.ctaData = payload
    },
    setPageList (state, value) {
      state.pageList = value
    },
    setResizeTimestamp (state, value) {
      state.resizeTimestamp = value
    },
    setCaptureImageTimestamp (state, value) {
      state.captureImageTimestamp = value
    },
    setPageAddGuideContent (state, values) {
      state.pageAddGuideFocusContent = values
    },
    setABList (state, payload) {
      state.abList = payload
    },
    setABPageName (state, payload) {
      state.abPageName = payload
    },
    setStickyData (state, payload) {
      state.stickyData = payload
    },
    setStickyDataId (state, payload) {
      state.stickyDataId = payload
    },
    setShowStickyData (state, payload) {
      state.showStickyData = payload
    },
    setShowNoPvDialog (state, payload) {
      state.showNoPvDialog = payload
    },
    setShowABTestTip (state, payload) {
      state.showABTestTip = payload
    },
    setMyInfoTimestamp (state, payload) {
      state.myInfoTimestamp = payload
    },
    setDreamVersion (state, payload) {
      state.dreamVersion = payload
    },
    setIsABPlan (state, payload) {
      state.isABPlan = payload
    },
    clearAllUserData (state) {
      state.userData = null
      state.planData = null
      state.stickyData = null
      state.stickyDataId = null
      state.showStickyData = true
      state.showNoPvDialog = true
      state.myInfoTimestamp = null
    },
    setTimeseriesData (state, payload) {
      state.timeseriesData = payload
    },
    clearTimeseriesData (state) {
      state.timeseriesData = null
    },
    setFreqRangeSelect (state, payload) {
      state.freqRangeSelect = payload
    },
    clearFreqRangeSelect (state) {
      state.freqRangeSelect = {}
    },
    setFreqSelect (state, payload) {
      state.freqSelect = payload
    },
    clearFreqSelect (state) {
      state.freqSelect = {}
    },
    setFreqRange (state, payload) {
      state.freqRange = payload
    },
    setCxReportId (state, payload) {
      state.cxReportId = payload
    },
    setAbEditValues (state, payload) {
      state.abEditValues = payload
    },
    clearAbEditValues (state) {
      state.abEditValues = null
    },
    setSelectedReport (state, payload) {
      state.selectedReport = [...state.selectedReport, payload]
    },
    clearSelectedReport (state) {
      state.selectedReport = []
    },
    removeSelected (state, report) {
      state.selectedReport = state.selectedReport.filter(
        item => item.id !== report.id
      )
    },
    setDraggingItem (state, payload) {
      state.draggingItem = payload
    },
    clearDraggingItem (state) {
      state.draggingItem = {}
    },
    setIsChatOpened (state, payload) {
      state.isChatOpened = payload
    },
    setTotalReportCnt (state, payload) {
      state.totalReportCnt = payload
    },
    setTotalRemainJam (state, value) {
      state.totalRemainJam = value
    },
    setApplyBasketCount (state, value) {
      state.applyBasketCount = value
    },
    setJamListFilter (state, value) {
      state.jamListFilter = value
    }
  },
  actions: {
    storeMineCnt ({ commit }, lang) {
      getMineCount(lang).then(res => {
        commit('setTotalReportCnt', res.result.count)
      })
    },
    storeUserData ({ dispatch, commit }) {
      return getUserInfo().then(data => {
        commit('setUserData', data)
        return data
      })
    },
    storePlanData ({ commit, state }) {
      const handlePlanData = (data) => {
        const planData = getNormalizedPlanData(data)
        let stickyDataId = null

        if (planData && planData.stickyData) {
          stickyDataId = planData.stickyData.id
          const oldStickyDataId = state.stickyDataId

          if (stickyDataId !== oldStickyDataId) {
            commit('setShowStickyData', true)
          }
        }
        commit('setStickyDataId', stickyDataId)
        commit('setPlanData', planData)
        commit('setIsABPlan', planData.planNumber === AB_PLAN_NUMBER)
      }

      return getCurrentNext().then(data => {
        handlePlanData(data)
        return data
      }).catch(error => {
        if (error.message === 'No Plan found') {
          handlePlanData({})
        } else {
          throw error
        }
      })
    },
    storeABList ({ commit, state }, aid) {
      const applyData = state.applyData

      return getABList(aid).then(data => {
        const urlList = (data.url || []).filter(item => !item.deleted)
        for (let i = 0; i < urlList.length; i++) {
          const item = urlList[i]
          item.abTestType = 'url'
          item.abTestTypeName = 'URL Split'
        }
        const textList = (data.text || []).filter(item => !item.deleted)
        for (let i = 0; i < textList.length; i++) {
          const item = textList[i]
          item.abTestType = 'text'
          item.abTestTypeName = 'UX Writing'
        }
        data = [...urlList, ...textList]

        for (let i = 0, iLen = data.length; i < iLen; i++) {
          const item = data[i]
          item.canViewReport = false
          if (item.installDate && ['run', 'stop', 'endByPvLimit', 'endByAnalysisDate', 'tempStop'].includes(item.uiState)) {
            item.canViewReport = true
          }
          item.canRunChange = false
          if (['stop'].includes(item.uiState)) {
            item.canRunChange = true
          }

          item.canStopChange = false
          if (['tempStop', 'run', 'analyzing', 'uninstall', 'error', 'scheduledRun'].includes(item.uiState)) {
            item.canStopChange = true
          }

          if (['run', 'error', 'uninstall'].includes(applyData.state) && applyData.creditEmpty) {
            if (['run', 'analyzing', 'uninstall', 'error'].includes(item.uiState)) {
              item.uiState = 'tempStop'
            }
          } else {
            if (applyData.state === 'stop' && ['run', 'error', 'analyzing'].includes(item.uiState)) {
              if (item.state === 'run') {
                item.canViewReport = true
              }
              item.uiState = 'tempStop'
            }
          }
        }

        commit('setABList', data)
        return data
      })
    }
  },
  strict: IS_PRODUCTION
})

export default store

export function getStore () {
  return store
}
